import React from "react"

import { SelectDropdown, Tooltip } from "@coval/ui"
import { Info } from "lucide-react"

import { isJsonObjectValue, type JsonObject } from "@/types/json"

import { SimulationWrapperType } from "../types"

interface CustomAction {
  key: string
  label: string
  description: string
}

const PRE_SIMULATION_ACTIONS: CustomAction[] = [
  {
    key: "enroll_only",
    label: "Enroll Only",
    description:
      "Enrolls a new user. Access via {{sim_wrapper.chime.user}}, {{sim_wrapper.chime.card_last_4}}, {{sim_wrapper.chime.email}}, {{sim_wrapper.chime.phone}}.",
  },
  {
    key: "enroll_and_activate_credit",
    label: "Enroll with Credit Account",
    description:
      "Enrolls a new user and activates a secured credit account. Access via {{sim_wrapper.chime.user}}, {{sim_wrapper.chime.card_last_4}}, {{sim_wrapper.chime.email}}.",
  },
  {
    key: "enroll_and_activate_cards",
    label: "Enroll with Activate Debit",
    description:
      "Enrolls a new user and activates their debit card. Access via {{sim_wrapper.chime.user}}, {{sim_wrapper.chime.card_last_4}}, {{sim_wrapper.chime.email}}.",
  },
  {
    key: "freeze",
    label: "Freeze Card",
    description:
      "Freezes card transactions for a preconfigured user. Requires card_product_type in metadata.",
  },
  {
    key: "unfreeze",
    label: "Unfreeze Card",
    description:
      "Unfreezes card transactions. Requires card_product_type in metadata.",
  },
  {
    key: "enable_intl",
    label: "Enable International",
    description:
      "Enables international transactions. Requires card_product_type in metadata.",
  },
  {
    key: "disable_intl",
    label: "Disable International",
    description:
      "Disables international transactions. Requires card_product_type in metadata.",
  },
  {
    key: "get_cards",
    label: "Get Cards",
    description:
      "Retrieves card info via GetCards API. Observation stored under get_cards key.",
  },
  {
    key: "get_account",
    label: "Get Account",
    description:
      "Retrieves account info via GetAccount API. Observation stored under get_account key.",
  },
  {
    key: "api",
    label: "Generic API Call",
    description:
      "Generic API call. Requires action_config with api_type, api_url, request_body, and method.",
  },
]

const POST_SIMULATION_ACTIONS: CustomAction[] = [
  {
    key: "get_cards",
    label: "Get Cards",
    description:
      "Retrieves card info via GetCards API. Observation stored under get_cards key.",
  },
  {
    key: "get_account",
    label: "Get Account",
    description:
      "Retrieves account info via GetAccount API. Observation stored under get_account key.",
  },
  {
    key: "api",
    label: "Generic API Call",
    description:
      "Generic API call. Requires action_config with api_type, api_url, request_body, and method.",
  },
]

const INFO_CONTENT = `Actions populate attributes accessible via template variables:
• {{sim_wrapper.chime.user}} — User ID
• {{sim_wrapper.chime.card_last_4}} — Card last 4
• {{sim_wrapper.chime.email}} — Email
• {{sim_wrapper.chime.phone}} — Phone
• {{sim_wrapper.chime.password}} — Password
• {{sim_wrapper.chime.socialSecurityNumberLast4}} — SSN last 4

Observations are stored per action and accessible in metrics via match_path (e.g. get_cards.response.cards[0].status).`

interface CustomTestCaseCellProps {
  simulationMetadataInput: JsonObject | null | undefined
  onUpdate: (updates: JsonObject) => void
  isExpanded: boolean
  width: string
}

export const CustomTestCaseCell: React.FC<CustomTestCaseCellProps> = ({
  simulationMetadataInput,
  onUpdate,
  isExpanded,
  width,
}) => {
  const metadata: JsonObject = simulationMetadataInput || {}
  const customData = isJsonObjectValue(metadata[SimulationWrapperType.CUSTOM])
    ? metadata[SimulationWrapperType.CUSTOM]
    : {}

  // Only read from wrapped format: { chime: { pre_simulation: [...], post_simulation: [...] } }
  let preSimulationList: JsonObject[] = []
  let postSimulationList: JsonObject[] = []

  if (metadata[SimulationWrapperType.CUSTOM]) {
    const customConfig = customData as {
      pre_simulation?: JsonObject[]
      post_simulation?: JsonObject[]
    }
    preSimulationList = Array.isArray(customConfig.pre_simulation)
      ? customConfig.pre_simulation
      : []
    postSimulationList = Array.isArray(customConfig.post_simulation)
      ? customConfig.post_simulation
      : []
  }

  const preSimulation = preSimulationList[0] || {}
  const postSimulation = postSimulationList[0] || {}

  const preSimulationAction = (preSimulation.action as string) || ""
  const postSimulationAction = (postSimulation.action as string) || ""

  const handlePreSimulationActionChange = (value: string) => {
    const newPreSimulation = { ...preSimulation }
    if (value) {
      newPreSimulation.action = value
    } else {
      delete newPreSimulation.action
    }
    const newPreSimulationList = value
      ? [newPreSimulation, ...preSimulationList.slice(1)]
      : []
    onUpdate({
      [SimulationWrapperType.CUSTOM]: {
        ...customData,
        pre_simulation: newPreSimulationList,
        post_simulation: postSimulationList,
      },
    })
  }

  const handlePostSimulationActionChange = (value: string) => {
    const newPostSimulation = { ...postSimulation }
    if (value) {
      newPostSimulation.action = value
    } else {
      delete newPostSimulation.action
    }
    const newPostSimulationList = value
      ? [newPostSimulation, ...postSimulationList.slice(1)]
      : []
    onUpdate({
      [SimulationWrapperType.CUSTOM]: {
        ...customData,
        pre_simulation: preSimulationList,
        post_simulation: newPostSimulationList,
      },
    })
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isExpanded) {
      e.stopPropagation()
    }
  }

  return (
    <td
      className="p-3"
      style={{ width, verticalAlign: "top" }}
      onClick={handleClick}
    >
      <div className="bg-surface-1 rounded p-2 hover:bg-surface-2 transition-colors space-y-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-coval-text-muted font-medium">
            Wrapper Actions
          </span>
          <Tooltip
            content={
              <div className="max-w-xs p-2 text-xs whitespace-pre-line">
                {INFO_CONTENT}
              </div>
            }
            side="left"
          >
            <Info size={14} className="text-coval-text-muted shrink-0" />
          </Tooltip>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs text-coval-text-muted">Pre-Simulation</span>
          {/* Stop bubbling so opening the dropdown doesn't trigger the row click. */}
          <div role="presentation" onClick={(e) => e.stopPropagation()}>
            <SelectDropdown
              aria-label="Pre-Simulation"
              value={preSimulationAction}
              onValueChange={handlePreSimulationActionChange}
              placeholder="None"
              options={PRE_SIMULATION_ACTIONS.map((action) => ({
                value: action.key,
                label: action.label,
                description: action.description,
              }))}
            />
          </div>
        </div>
        {preSimulationAction && (
          <p className="text-xs text-coval-text-muted">
            {
              PRE_SIMULATION_ACTIONS.find((a) => a.key === preSimulationAction)
                ?.description
            }
          </p>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-xs text-coval-text-muted">Post-Simulation</span>
          {/* Stop bubbling so opening the dropdown doesn't trigger the row click. */}
          <div role="presentation" onClick={(e) => e.stopPropagation()}>
            <SelectDropdown
              aria-label="Post-Simulation"
              value={postSimulationAction}
              onValueChange={handlePostSimulationActionChange}
              placeholder="None"
              options={POST_SIMULATION_ACTIONS.map((action) => ({
                value: action.key,
                label: action.label,
                description: action.description,
              }))}
            />
          </div>
        </div>
        {postSimulationAction && (
          <p className="text-xs text-coval-text-muted">
            {
              POST_SIMULATION_ACTIONS.find(
                (a) => a.key === postSimulationAction,
              )?.description
            }
          </p>
        )}
      </div>
    </td>
  )
}
